import { useEffect, useState } from "react";
import { LuArrowLeft, LuInfo, LuMessageSquare, LuX } from "react-icons/lu";
import Preloader from "../../../app-components/preloader/Preloader";
import apiClient from "../../../api-services/ApiClient";
import { Link, useParams } from "react-router-dom";
import { useGlobalState } from "../../../context/GlobalStateContext";
import { OptionProps } from "../../../interface-and-types";
import {
  ActionTrackerInterface,
  ActionTrackerSchema,
} from "../../../interface-and-types/inspection.schema";
import { SITELINKS } from "../../../app-routes/links";
import { formatDate, truncateText } from "../../../helpers";
import { toast, ToastContainer } from "react-toastify";

const AdminViewActionTracker = () => {
  const { id } = useParams() ?? "";
  const { state } = useGlobalState();
  const [loading, setLoading] = useState<boolean>(false);
  const [actionTracker, setActionTracker] = useState<any>({});
  const [actionTrackerComment, setActionTrackerComment] = useState<any>([]);
  const [comment, setComment] = useState<any>("");
  const [rejectComment, setRejectComment] = useState<any>("");
  const [vessels, setVessels] = useState<OptionProps[]>([]);
  const [form, setForm] = useState<Partial<ActionTrackerInterface>>({});
  const [criticalEquipmentList, setCriticalEquipmentList] = useState<any[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);
  const [marineSuperintendent, setMarineSuperintendent] = useState<any[]>([]);
  const [equipmentList, setEquipmentList] = useState<any[]>([]);
  const [priority, setPriority] = useState<any[]>([]);
  const [serverError, setServerError] = useState<string>("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ActionTrackerInterface, string>>
  >({});

  useEffect(() => {
    if (id) {
      getActionTracker(id);
      getActionTrackerComment(id);
    }
  }, [id]);

  useEffect(() => {
    getVessels();
    getCriticalEquipmentTypes();
    getDefectType();
    getMarineSuperintendent();
    getPriorities();
    getEquipmentList();
  }, []);

  const handleInput = (e: any) => {
    setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getVessels = async () => {
    try {
      // const res: any = await apiClient.get("UserVessels/Assignments");
      const res: any = await apiClient.get(
        "Vessels?PageSize=1000&view=withuser"
      );
      if (res && res.data) {
        const options: OptionProps[] = res.data.data.map(
          (i: any, index: number) => ({
            label: i.name,
            value: i.id,
            name: "Vessels",
            key: index,
          })
        );
        setVessels(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data.filter((item: any) => !item.isDeleted));
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMarineSuperintendent = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        "users?Rolename=MarineSuperintendent&pageSize=1000"
      );
      setMarineSuperintendent(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriority(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const formData = { ...form };

    if (files) {
      const filesArray = Array.from(files);

      const base64Promises = filesArray.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          base64Image: base64,
          mimeType: file.type,
        };
      });

      // Convert all files to Base64
      Promise.all(base64Promises).then((base64Arr: any) => {
        formData.defectCheckListAttachments = base64Arr;
        setForm(formData);
      });
    }
  };

  const handleUpdate = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = ActionTrackerSchema.safeParse(form);

    if (result.success) {
      setLoading(true);
      try {
        const data = {
          vesselDefectCheckListId: actionTracker?.vesselDefectCheckListId,
          responsibleParty: result.data.responsibleParty,
          resourcesNeeded: result.data.resourcesNeeded,
          targetCloseOutDate: result.data.targetCloseOutDate,
          // dateClosed: result.data.dateClosed,
          descriptionOfDefect: result.data.descriptionOfDefect,
          percentageOfActionClosedOut: result.data.percentageOfActionClosedOut,
          defectCheckListAttachments: result.data.defectCheckListAttachments,
        };

        await apiClient.patch(
          `ActionTracker/${id}/CloseOutAction/Approve`,
          data
        );
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<
        Record<keyof ActionTrackerInterface, string>
      > = {};

      result.error.errors.forEach((err: any) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof ActionTrackerInterface;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
      setServerError(
        "Form validation error occured! Kindly complete all required fields."
      );
    }
  };

  const getActionTracker = async (id: string) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(`ActionTracker/${id}`);
      if (res && res.data) {
        const data = res.data.data.vesselDefectCheckList;
        const responsibleParty = res.data.data.responsibleParty;
        const resourcesNeeded = res.data.data.resourcesNeeded;
        const targetCloseOutDate = res.data.data.targetCloseOutDate;
        const dateClosed = res.data.data.dateClosed;
        const descriptionOfDefect = res.data.data.descriptionOfDefect;
        const percentageOfActionClosedOut =
          res.data.data.percentageOfActionClosedOut;

        setActionTracker(res.data.data);
        setForm({
          vesselId: data.vesselId,
          equipmentName: data?.equipmentName,
          equipmentId: data?.equipment?.id,
          criticalEquipment: data?.criticalEquipment,
          defectTypeId: data?.defectTypeId,
          marineSuperintendentId: data?.marineSuperintendentId,
          comments: data?.comments,
          priority: data?.priority,
          responsibleParty: responsibleParty,
          resourcesNeeded: resourcesNeeded,
          targetCloseOutDate: targetCloseOutDate,
          dateClosed: dateClosed,
          descriptionOfDefect: descriptionOfDefect,
          percentageOfActionClosedOut: percentageOfActionClosedOut,
          defaultDefectCheckListAttachments: data?.defectCheckListAttachments,
        });
      }
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionTrackerComment = async (id: string) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        `ActionTracker/${id}/TrackerComment`
      );
      if (res && res.data) {
        setActionTrackerComment(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const patchActionTrackerComment = async (id: string) => {
    setLoading(true);
    try {
      const data = {
        comment: comment,
      };
      await apiClient.patch(`ActionTracker/${id}/Comment`, data);

      getActionTrackerComment(id);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const actionColor = (status: string) => {
    if (status) {
      switch (status.toLowerCase()) {
        case "pending":
          return "blue";
        case "closed":
          return "green";
        case "error":
          return "red";
        default:
          return "slate";
      }
    }

    return "slate";
  };

  const declineAction = async () => {
    setLoading(true);
    try {
      const data = {
        comment: rejectComment,
      };
      await apiClient.patch(`ActionTracker/${id}/CloseOutAction/Reject`, data);
      toast.success("Rejected!", {
        autoClose: 60000,
        theme: "colored", // Options: "light", "dark", "colored"
      });
    } catch (error) {
      setServerError(JSON.stringify(error));
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getEquipmentList = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Equipment");
      setEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center w-full">
          <h4 className="font-medium lg:text-sm text-gray-100 flex gap-3 items-center">
            <Link to={SITELINKS.MARINE_ACTION_TRACKER} className="text-[20px]">
              <LuArrowLeft />
            </Link>
            <span>
              View Action Tracker /{" "}
              {actionTracker?.vesselDefectCheckList?.vessel?.name}
            </span>
          </h4>

          {/*page actions */}
          <p className="font-medium 2xl:text-sm text-sm text-gray-50 flex gap-2 items-center">
            Status:{" "}
            <span
              className={`text-${actionColor(
                actionTracker?.closeOutAction
              )}-200 font-medium uppercase`}
            >
              {actionTracker?.closeOutAction}
            </span>
          </p>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-105px]">
        <div className="w-full p-6 rounded-lg shadow bg-white border">
          <form className="w-full">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 w-full">
              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Vessel Name
                </label>
                <select
                  name="vesselId"
                  value={form?.vesselId}
                  className="disabled:bg-gray-100 w-full p-2 text-[12px] border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  onChange={handleInput}
                  disabled
                >
                  <option value={""}>Select</option>
                  {vessels.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.vesselId}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Equpiment Name
                </label>
                <select
                  name="equipmentId"
                  value={form?.equipmentId}
                  disabled
                  className="disabled:bg-gray-100 w-full p-2 text-[12px] border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  onChange={handleInput}
                >
                  <option value={""}>Select</option>
                  {equipmentList.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {item?.name}
                    </option>
                  ))}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.equipmentId}
                </span>

                {/* <input
                  name="equipmentName"
                  value={form.equipmentName}
                  type="text"
                  placeholder=""
                  onChange={handleInput}
                  className="disabled:bg-gray-100 w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  disabled
                />
                <span className="text-red-600 text-[10px]">
                  {errors.equipmentName}
                </span> */}
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Critical Equipment
                </label>
                <select
                  name="criticalEquipment"
                  className="disabled:bg-gray-100 w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  value={form?.criticalEquipment}
                  onChange={handleInput}
                  disabled
                >
                  <option value="">Select</option>
                  {criticalEquipmentList.map((item, i) => {
                    return (
                      <option key={i} value={item?.value}>
                        {item?.name}
                      </option>
                    );
                  })}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.criticalEquipment}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Type Of Defect
                </label>
                <select
                  name="defectTypeId"
                  className="disabled:bg-gray-100 w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  value={form?.defectTypeId}
                  onChange={handleInput}
                  disabled
                >
                  <option value="">Select</option>
                  {defectType.map((item, i) => {
                    return (
                      <option key={i} value={item?.id}>
                        {item?.name}
                      </option>
                    );
                  })}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.defectTypeId}
                </span>
              </div>

              <div className="hidden">
                <label className="text-gray-500 text-[10px] font-medium">
                  Select Marine Superintendent
                </label>
                <select
                  name="marineSuperintendentId"
                  className="disabled:bg-gray-100 w-full p-2 text-[12px] border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  value={form?.marineSuperintendentId}
                  onChange={handleInput}
                  disabled
                >
                  <option value="">Select</option>
                  {marineSuperintendent.map((item, i) => {
                    return (
                      <option key={i} value={item?.id}>
                        {item?.firstName} {item?.lastName}
                      </option>
                    );
                  })}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.marineSuperintendentId}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Priority
                </label>
                <select
                  name="priority"
                  className="disabled:bg-gray-100 w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  value={form?.priority}
                  onChange={handleInput}
                  disabled
                >
                  <option value="">Select</option>
                  {priority.map((item, i) => {
                    return (
                      <option key={i} value={item?.value}>
                        {item?.name} ({item?.value})
                      </option>
                    );
                  })}
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.priority}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Responsible Party
                </label>
                <input
                  name="responsibleParty"
                  value={form?.responsibleParty}
                  type="text"
                  placeholder=""
                  onChange={handleInput}
                  className="w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                />
                <span className="text-red-600 text-[10px]">
                  {errors.responsibleParty}
                </span>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="text-gray-500 text-[10px] font-medium">
                  Resources Needed
                </label>
                <select
                  name="resourcesNeeded"
                  className="disabled:bg-gray-100 w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  value={form?.resourcesNeeded}
                  onChange={handleInput}
                >
                  <option value="">Select</option>
                  <option value="Human">Human</option>
                  <option value="Material">Material</option>
                </select>

                <span className="text-red-600 text-[10px]">
                  {errors.resourcesNeeded}
                </span>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="text-gray-500  text-[10px] font-medium">
                  Write a comment
                </label>
                <textarea
                  disabled
                  name="comments"
                  value={form?.comments}
                  placeholder=""
                  onChange={handleInput}
                  className="disabled:bg-gray-100 h-[60px] w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                ></textarea>
                <span className="text-red-600 text-[10px]">
                  {errors.comments}
                </span>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="text-gray-500 text-[10px] font-medium">
                  Description of Defect
                </label>
                <textarea
                  name="descriptionOfDefect"
                  value={form?.descriptionOfDefect}
                  placeholder=""
                  onChange={handleInput}
                  className="h-[60px] w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                ></textarea>
                <span className="text-red-600 text-[10px]">
                  {errors.descriptionOfDefect}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Target Close-out Date
                </label>
                <input
                  name="targetCloseOutDate"
                  value={form?.targetCloseOutDate}
                  type="text"
                  placeholder=""
                  onChange={handleInput}
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => (e.target.type = "text")}
                  className="w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                />
                <span className="text-red-600 text-[10px]">
                  {errors.targetCloseOutDate}
                </span>
              </div>

              <div className="">
                <label className="text-gray-500 text-[10px] font-medium">
                  Close-out Date
                </label>
                <input
                  disabled
                  name="dateClosed"
                  value={form?.dateClosed}
                  type="text"
                  placeholder=""
                  onChange={handleInput}
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => (e.target.type = "text")}
                  className="w-full p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                />
                <span className="text-red-600 text-[10px]">
                  {errors.dateClosed}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Action Close-Out Rate (%)
                  <span className="text-red-600 text-[10px]">(*)</span>
                </label>
                <select
                  name="percentageOfActionClosedOut"
                  value={form?.percentageOfActionClosedOut}
                  className="w-full py-2 p-2 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  onChange={handleInput}
                >
                  <option value={""}>Select</option>
                  <option value={0}>Not Started (0%)</option>
                  <option value={30}>Between 1% - 30%</option>
                  <option value={60}>Between 31% - 60%</option>
                  <option value={90}>Between 61% - 90%</option>
                  <option value={100}>Completed (100%)</option>
                </select>
                <span className="text-red-600 text-[10px]">
                  {errors.percentageOfActionClosedOut}
                </span>
              </div>

              <div>
                <label className="text-gray-500 text-[10px] font-medium">
                  Attach Pictures
                </label>
                <input
                  multiple
                  type="file"
                  id="file-upload"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e)}
                  className="w-full p-2 py-1.5 text-xs border rounded focus:outline-none focus:ring focus:ring-blue-500"
                />
                <span className="text-red-600 text-[10px]">
                  {errors.percentageOfActionClosedOut}
                </span>
              </div>
            </div>

            <div className="mt-4">
              {form?.defaultDefectCheckListAttachments && (
                <>
                  <p className="flex-col text-xs text-gray-500 mb-2 font-medium">
                    Previously Attached Images
                  </p>
                  <div className="flex flex-wrap gap-4 sm:grid-cols-2 w-full">
                    {form?.defaultDefectCheckListAttachments.map(
                      (item, index) => (
                        <div className="relative text-xs">
                          <img
                            src={
                              item.base64Image?.includes("data:image/")
                                ? item?.base64Image
                                : `data:image/png;base64,${item?.base64Image}`
                            }
                            className="h-[50px] rounded-lg border bg-gray-100"
                            alt="my Attachment"
                          />
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="mt-4">
              {form.defectCheckListAttachments && (
                <>
                  <p className="flex-col text-xs text-gray-500 mb-2 font-medium">
                    Attached Images Preview
                  </p>
                  <div className="flex flex-wrap gap-4 sm:grid-cols-2 w-full">
                    {form.defectCheckListAttachments.map((img, index) => (
                      <div className="relative">
                        <span className="bg-purple-800 text-white rounded-full border absolute top-1 right-1 text-xs p-1 px-2 font-medium">
                          {index + 1}
                        </span>
                        <img
                          src={img.base64Image}
                          className="h-[100px] rounded-lg border bg-gray-100"
                          alt="base64"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="text-red-600 text-[10px] mt-4">{serverError}</div>

            <div className="col-span-1 sm:col-span-2 pt-4 mt-4 border-t">
              <label className="text-gray-500 text-[10px] font-medium">
                Rejection comment
              </label>
              <textarea
                name="comments"
                value={rejectComment}
                placeholder=""
                onChange={(e: any) => setRejectComment(e.target.value)}
                className="disabled:bg-gray-100 h-[60px] w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-xs"
              ></textarea>
            </div>

            {actionTracker?.closeOutAction !== "Approved" && (
              <div className="pt-8 lg:w-2/5 flex gap-3 text-xs hidden">
                <button
                  type="button"
                  onClick={declineAction}
                  className="flex w-full gap-2 items-center bg-red-700 shadow p-3 rounded-lg text-white font-medium px-4 w-1/4 justify-center"
                >
                  Reject Action Close-Out
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="flex w-full gap-2 items-center bg-green-700 shadow p-3 rounded-lg text-white font-medium px-4 w-1/4 justify-center"
                >
                  {Number(form?.percentageOfActionClosedOut) < 100
                    ? "Update Action Progress"
                    : "Approve Action Close-Out"}
                </button>
              </div>
            )}
          </form>
        </div>
      </section>

      {isModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col">
          <div className="bg-white rounded shadow-md w-full max-w-xs animate-modalShow bottom-[15%] right-[5%] fixed">
            <div className="w-full rounded-lg shadow bg-white border">
              <p className="bg-gray-100 text-slate-800 p-3 px-6 rounded-t-lg border-b text-md">
                In-App Chat
              </p>

              <div className="col-span-1 sm:col-span-2 p-3 border-b overflow-y-scroll max-h-[250px]">
                {actionTrackerComment?.map((item: any) => {
                  return (
                    <div
                      className={`mb-3 flex flex-col text-xs ${
                        state.tepnguser?.user?.email === item?.createdBy
                          ? "items-start"
                          : "items-end"
                      }`}
                    >
                      <p
                        className={`${
                          state.tepnguser?.user?.email === item?.createdBy
                            ? "text-start px-2"
                            : "text-end px-2"
                        }`}
                      >
                        <small>{truncateText(item?.createdBy, 20)}</small>
                      </p>

                      <div
                        className={`w-3/4 bg-gray-100 px-3 py-2 text-slate-800 text-[10px] font-normal ${
                          state.tepnguser?.user?.email === item?.createdBy
                            ? "rounded-tl-[0px]"
                            : "rounded-tr-[0px]"
                        }  rounded-xl`}
                      >
                        <p>{item?.comment}</p>

                        <p className="text-slate-600 text-[7px] text-end pt-2">
                          {formatDate(item?.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {!actionTrackerComment?.length && (
                  <>
                    <div className="text-[60px] flex justify-center mb-3 text-center text-red-700">
                      <LuInfo />
                    </div>
                    <p className="text-xs text-center px-16">
                      No Message available, send a message below
                    </p>
                  </>
                )}
              </div>

              {actionTracker?.closeOutAction !== "Approved" && (
                <div className="col-span-1 sm:col-span-2 p-3 hidden">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Send a message/note to Marine Superintendent"
                    className="font-normal rounded-sm h-[50px] w-full py-2 p-2 border focus:outline-none focus:ring focus:ring-blue-500 text-xs leading-normal"
                  ></textarea>

                  <div>
                    <button
                      className="bg-blue-800 text-white rounded p-2 w-full text-xs"
                      onClick={() => {
                        patchActionTrackerComment(id ?? "");
                      }}
                    >
                      Send Message
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="fixed right-[5%] bottom-[4.5%] z-50">
        <button
          onClick={() => setModalOpen(!isModalOpen)}
          className={`rounded-full ${
            !isModalOpen ? "bg-secondaryBlue" : "bg-white"
          } p-4 shadow-xl block hover:shadow-xl animate-animate-wave`}
        >
          {isModalOpen ? (
            <LuX className="text-[35px] text-red-700" />
          ) : (
            <LuMessageSquare className="text-[35px] text-slate-50" />
          )}
        </button>

        {!isModalOpen && (
          <span className="absolute z-10 bottom-[3%] w-16 h-16 bg-blue-500 rounded-full animate-wave opacity-75 pointer-events-none"></span>
        )}
      </section>

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </>
  );
};

export default AdminViewActionTracker;
